(function () {
  const roots = document.querySelectorAll('.fg');
  if (!roots.length) return;

  roots.forEach((root) => {
    const viewport = root.querySelector('.fg__viewport');
    const track    = root.querySelector('.fg__track');
    if (!viewport || !track) return;

    root.classList.add('is-hydrating');

    const gapPx   = parseInt(root.dataset.gap || '8', 10);
    const pScale  = (parseInt(root.dataset.passiveScale || '60', 10)) / 100;
    const aScale  = (parseInt(root.dataset.activeScale  || '200', 10)) / 100;
    const TRANS_MS = parseInt(root.dataset.transitionMs || '600', 10);

    root.style.setProperty('--gap', `${gapPx}px`);
    root.style.setProperty('--scale-passive', String(pScale));
    root.style.setProperty('--scale-active',  String(aScale));
    track.style.gap = `${gapPx}px`;

    const originals = Array.from(track.querySelectorAll('.fg__item'));
    const N = originals.length;
    if (!N) return;

    const initialOriginal = Math.max(0, Math.min(
      N - 1,
      parseInt(root.dataset.initialIndex || '0', 10)
    ));

    originals.forEach((el, i) => { el.dataset.sourceIndex = String(i); });

    const makeClone = (el, i) => {
      const c = el.cloneNode(true);
      c.classList.remove('is-active');
      c.dataset.clone = '1';
      c.dataset.sourceIndex = String(i);
      return c;
    };

    const fragBefore = document.createDocumentFragment();
    const fragAfter  = document.createDocumentFragment();
    originals.forEach((el, i) => fragBefore.appendChild(makeClone(el, i)));
    originals.forEach((el, i) => fragAfter.appendChild(makeClone(el, i)));
    track.insertBefore(fragBefore, track.firstChild);
    track.appendChild(fragAfter);

    let items = Array.from(track.querySelectorAll('.fg__item'));
    let index = N + initialOriginal;

    function setActive(i) {
      items.forEach((it) => it.classList.remove('is-active'));
      if (items[i]) items[i].classList.add('is-active');
    }

    function easeOutCubic(t){ return 1 - Math.pow(1 - t, 3); }

    function animateScrollTo(targetLeft, ms = TRANS_MS, easing = easeOutCubic) {
      const start = viewport.scrollLeft;
      const dist  = targetLeft - start;
      if (Math.abs(dist) < 0.5 || ms <= 0) { viewport.scrollLeft = targetLeft; return Promise.resolve(); }
      const t0 = performance.now();
      return new Promise((resolve) => {
        function step(t) {
          const p = Math.min(1, (t - t0) / ms);
          viewport.scrollLeft = start + dist * easing(p);
          if (p < 1) requestAnimationFrame(step); else resolve();
        }
        requestAnimationFrame(step);
      });
    }

    // --- smooth centering with silent normalization ---
    function centerItem(i, ms = TRANS_MS) {
      let target = (i + items.length) % items.length;
      const src = Number(items[target].dataset.sourceIndex ?? ((target % N + N) % N));
      const middleIdx = N + src;

      index = target;
      setActive(index);

      const vp = viewport.getBoundingClientRect();
      const el = items[index].getBoundingClientRect();
      const delta = (el.left + el.width / 2) - (vp.left + vp.width / 2);
      const targetLeft = viewport.scrollLeft + delta;

      animateScrollTo(targetLeft, ms).then(() => {
        root.classList.add('fg--freeze-anim');

        index = middleIdx;
        setActive(index);

        const vp2 = viewport.getBoundingClientRect();
        const el2 = items[index].getBoundingClientRect();
        const fix = (el2.left + el2.width / 2) - (vp2.left + vp2.width / 2);
        if (Math.abs(fix) > 0.5) viewport.scrollLeft += fix;

        requestAnimationFrame(() => {
          root.classList.remove('fg--freeze-anim');
        });
      });
    }

    // --- snap helper (used by touch) ---
    function snapToClosest() {
      const vpCenter = viewport.getBoundingClientRect().left + viewport.clientWidth / 2;
      let best = index, bestDist = Infinity;
      for (let off = -4; off <= 4; off++) {
        const j = (index + off + items.length) % items.length;
        const r = items[j].getBoundingClientRect();
        const cX = r.left + r.width / 2;
        const d = Math.abs(cX - vpCenter);
        if (d < bestDist) { bestDist = d; best = j; }
      }
      centerItem(best);
    }

    // --- click handling ---
    const isDesktopLike = () =>
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    track.addEventListener('click', (e) => {
      const fig = e.target.closest('.fg__item');
      if (!fig || !track.contains(fig)) return;

      const src = Number(fig.dataset.sourceIndex);
      if (!Number.isFinite(src)) return;

      e.preventDefault();

      // if clicking the already active item on desktop, allow alt-click to go prev/next
      const clickedIdx = items.indexOf(fig);
      if (isDesktopLike() && clickedIdx === index) {
        const dir = e.altKey ? -1 : 1;
        centerItem(index + dir);
        return;
      }

      centerItem(N + src);
    });

    // --- trackpad animation ---
    let wheelLocked = false;
    let gestureSilenceTimer = null;
    let stepsInGesture = 0;

    const SILENCE_RESET_MS = 100; // gesture ends after this quiet period
    const MIN_GAP_MS       = 120; // short cooldown to prevent double step

    viewport.addEventListener('wheel', (e) => {
      if (!isDesktopLike()) return;

      const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
      if (!isVertical) return;

      e.preventDefault();

      clearTimeout(gestureSilenceTimer);
      gestureSilenceTimer = setTimeout(() => {
        stepsInGesture = 0;
        wheelLocked = false;
      }, SILENCE_RESET_MS);

      if (!wheelLocked && stepsInGesture === 0) {
        stepsInGesture = 1;
        wheelLocked = true;

        const dir = e.deltaY > 0 ? 1 : -1;
        centerItem(index + dir);

        setTimeout(() => { wheelLocked = false; }, MIN_GAP_MS);
      }
    }, { passive: false });

    // --- touch (mobile swipe/tap) ---
    let tStartX = 0, tStartY = 0, tStartLeft = 0, tStartTime = 0, tMoved = false, intent = null;
    const SWIPE_DIST  = 25;
    const SWIPE_SPEED = 0.45;

    viewport.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      tStartX = t.clientX;
      tStartY = t.clientY;
      tStartLeft = viewport.scrollLeft;
      tStartTime = performance.now();
      tMoved = false;
      intent = null;
    }, { passive: true });

    viewport.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      const dx = t.clientX - tStartX;
      const dy = t.clientY - tStartY;

      if (!intent) {
        if (Math.abs(dx) > 6) intent = 'x';
        else if (Math.abs(dy) > 6) intent = 'y';
      }

      if (intent === 'x') {
        e.preventDefault();
        viewport.scrollLeft = tStartLeft - dx;
        tMoved = true;
      }
    }, { passive: false });

    viewport.addEventListener('touchend', (e) => {
      const dt = performance.now() - tStartTime;
      const c  = e.changedTouches[0];
      const dx = c.clientX - tStartX;
      const dy = c.clientY - tStartY;
      const vx = Math.abs(dx) / Math.max(dt, 1);

      const isHorizontal = (intent === 'x') || Math.abs(dx) > Math.abs(dy);

      if (isHorizontal && (Math.abs(dx) > SWIPE_DIST || vx > SWIPE_SPEED)) {
        centerItem(index + (dx < 0 ? 1 : -1));
        return;
      }
      if (tMoved || Math.abs(dx) > 12) {
        snapToClosest();
      }
    }, { passive: true });

    // --- keep centered on resize ---
    const ro = new ResizeObserver(() => centerItem(index, 0)); 
    ro.observe(viewport);

    function whenImagesReady(node, timeoutMs = 1200) {
      const imgs = Array.from(node.querySelectorAll('img'));
      if (!imgs.length) return Promise.resolve();
      const pending = imgs.filter(img => !(img.complete && img.naturalWidth));
      if (!pending.length) return Promise.resolve();
      return new Promise(resolve => {
        let resolved = false;
        const done = () => { if (!resolved) { resolved = true; resolve(); } };
        const to = setTimeout(done, timeoutMs);
        let left = pending.length;
        pending.forEach(img => {
          img.addEventListener('load', () => { if (--left === 0) { clearTimeout(to); done(); } }, { once: true });
          img.addEventListener('error', () => { if (--left === 0) { clearTimeout(to); done(); } }, { once: true });
        });
      });
    }

    (async () => {
      items = Array.from(track.querySelectorAll('.fg__item'));
      await whenImagesReady(track);
      requestAnimationFrame(() => {
        centerItem(index, 0);
        requestAnimationFrame(() => {
          root.classList.remove('is-hydrating');
          root.classList.add('is-ready');
        });
      });
    })();
  });
})();
